import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AuthContext, useAuth } from "@/context/AuthContext";
import { getActAs, setActAs, setActScope, runSwitchHook } from "@/lib/actAs";
import { useI18n } from "@/context/I18nContext";

const ME = "__me__";

const ActAsBar = ({ act, onChange, busy }) => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/act-as/resellers").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <div className={`border-b ${act ? "bg-amber-50 border-amber-300" : "bg-muted/30 border-border"}`} data-testid="act-as-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-2 text-sm">
        <UserCog className={`w-4 h-4 ${act ? "text-amber-700" : "text-muted-foreground"}`} />
        <span className={act ? "font-semibold text-amber-800" : "text-muted-foreground"}>{t("Saisir pour le compte de")} :</span>
        <select value={act?.id || ME} onChange={(e) => onChange(e.target.value === ME ? null : list.find((r) => r.id === e.target.value))}
          disabled={busy} className="h-8 rounded-md border border-input bg-background px-2 text-sm max-w-[22rem]" data-testid="act-as-select">
          <option value={ME}>{t("Moi-même")}</option>
          {act && !list.some((r) => r.id === act.id) && <option value={act.id}>{act.name}</option>}
          {list.map((r) => <option key={r.id} value={r.id}>{r.name}{r.company ? ` · ${r.company}` : ""}</option>)}
        </select>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-amber-700" data-testid="act-as-switching" />}
        {act && <span className="text-xs text-amber-800" data-testid="act-as-note">{t("Tarifs, marge et secteur de ce revendeur ; le devis sera à son nom.")}</span>}
      </div>
    </div>
  );
};

// Configurator pages: a manager / admin can work as a reseller (backend applies X-Act-As on configurator routes only).
export const ActAsScope = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMgr = auth.user?.role === "admin" || !!auth.user?.sales_manager;
  const [act, setAct] = useState(() => (isMgr ? getActAs() : null));
  const [profile, setProfile] = useState(null);
  setActScope(!!act);
  useEffect(() => { setActScope(!!act); return () => { setActScope(false); }; }, [act?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setProfile(null);
    if (!act) { return; }
    api.get("/auth/me", { headers: { "X-Act-As": act.id } }).then(({ data }) => setProfile(data))
      .catch(() => { setActAs(null); setAct(null); toast.error("Revendeur indisponible"); });
  }, [act?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [switching, setSwitching] = useState(false);
  if (!isMgr) return children;
  const change = async (r) => {
    const v = r ? { id: r.id, name: r.name } : null;
    setSwitching(true);
    let to = null;
    try { to = await runSwitchHook(v); } catch (e) {
      setSwitching(false);
      toast.error(e?.response?.data?.detail || "Changement de revendeur impossible");
      return;
    }
    setActAs(v); setAct(v); setSwitching(false);
    navigate(to || (location.pathname === "/" ? "/" : location.pathname + location.search));
    if (to) toast.success(`Devis transféré à ${v ? v.name : auth.user?.name || "vous"}`);
  };
  return (
    <>
      <ActAsBar act={act} onChange={change} busy={switching} />
      {act && !profile ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <AuthContext.Provider value={act ? { ...auth, user: profile } : auth}>
          <div key={act?.id || ME}>{children}</div>
        </AuthContext.Provider>
      )}
    </>
  );
};
